import React, { useState, useEffect } from "react";
import { FiX, FiRefreshCw } from "react-icons/fi";
import { format } from "date-fns";
import { DataState } from "../../context/DataProvider";
import { getExecutionHistory } from "../../services/workflowApi";
import { toast } from "react-hot-toast";
import { useWorkflowExecutions } from "../../context/WorkflowExecutionsProvider";

const ExecutionHistorySidebar = ({ workflowId, onClose, executionId, setExecutionId }) => {
  const [fetchedExecutions, setFetchedExecutions] = useState([]);
  const [mergedExecutions, setMergedExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { userGoogleId } = DataState();
  const { workflowExecutions } = useWorkflowExecutions();

  // Fetch initial execution history for the given workflow.
  const fetchExecutionHistory = async () => {
    setLoading(true);
    try {
      const data = await getExecutionHistory(workflowId, userGoogleId);
      // Map API _id to execution_id for consistency.
      const executions = data.executions.map(exec => ({
        ...exec,
        execution_id: exec._id,
      }));
      setFetchedExecutions(executions);
    } catch (error) {
      toast.error(error);
      console.error("Error fetching execution history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutionHistory();
  }, [workflowId]);

  // Merge the fetched executions with live socket updates.
  useEffect(() => {
    // Filter socket executions for the current workflow.
    const filteredSocketExecutions = workflowExecutions.filter(exec => exec.workflow_id === workflowId);
    // Create a mapping using execution_id as key.
    const execMap = {};
    fetchedExecutions.forEach(exec => {
      execMap[exec.execution_id] = exec;
    });
    // Update status or add new executions from socket updates.
    filteredSocketExecutions.forEach(socketExec => {
      if (execMap[socketExec.execution_id]) {
        execMap[socketExec.execution_id] = { ...execMap[socketExec.execution_id], ...socketExec };
      } else {
        execMap[socketExec.execution_id] = { ...socketExec };
      }
    });
    // Convert map back to an array and sort:
    // Executions without a timestamp are placed first.
    // Those with timestamps are sorted in descending order.
    const mergedArray = Object.values(execMap).sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return -1; // a has no timestamp -> comes first
      if (!b.timestamp) return 1;  // b has no timestamp -> comes first
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
    setMergedExecutions(mergedArray);
  }, [fetchedExecutions, workflowExecutions, workflowId]);

  // Set the hoveredIndex to the card matching the passed executionId, if available.
  useEffect(() => {
    if (executionId && mergedExecutions.length > 0) {
      const index = mergedExecutions.findIndex(exec => exec.execution_id === executionId);
      if (index !== -1) {
        setHoveredIndex(index);
      }
    }
  }, [mergedExecutions, executionId]);

  // Card stacking configuration.
  const fullHeight = 100; // full card height (px)
  const collapsedPeek = 20; // visible peek when not hovered

  const computeTranslateY = (index) => {
    if (hoveredIndex === null) {
      return index * collapsedPeek;
    } else {
      if (index <= hoveredIndex) {
        return index * collapsedPeek;
      } else {
        const distance = index - hoveredIndex;
        let extraOffset = fullHeight;
        if (distance === 2) extraOffset += 10;
        else if (distance >= 3) extraOffset += 15;
        return hoveredIndex * collapsedPeek + extraOffset + (distance - 1) * collapsedPeek;
      }
    }
  };

  const computeStyle = (index) => {
    const translateY = computeTranslateY(index);
    const distanceFromHovered = hoveredIndex === null ? 0 : Math.min(Math.abs(index - hoveredIndex), 3);
    let scale = 1, opacity = 1;
    if (hoveredIndex !== null) {
      if (distanceFromHovered === 0) {
        scale = 1.05;
        opacity = 1;
      } else if (distanceFromHovered === 1) {
        scale = 0.95;
        opacity = 0.9;
      } else if (distanceFromHovered >= 2) {
        scale = 0.9;
        opacity = 0.8;
      }
    }

    let transform = `translateY(${translateY}px) scale(${scale})`;
    if (hoveredIndex !== null && index === hoveredIndex) {
      transform += " rotateX(3deg) rotateY(3deg) translateZ(10px)";
    } else {
      transform += " translateZ(0)";
    }

    const zIndex =
      hoveredIndex === null
        ? mergedExecutions.length - index
        : index === hoveredIndex
        ? 100
        : 100 - Math.abs(index - hoveredIndex);

    return {
      transform,
      opacity,
      zIndex,
      height: `${fullHeight}px`,
      transformStyle: "preserve-3d",
    };
  };

  // Utility for status chip styling.
  const getStatusChipClasses = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
        return "bg-green-600 text-white";
      case "failed":
        return "bg-red-600 text-white";
      case "running":
        return "bg-blue-600 text-white";
      default:
        return "bg-yellow-600 text-white";
    }
  };

  // Clicking a card sets the current execution id.
  const handleExecutionClick = (execution) => {
    setExecutionId(execution.execution_id);
  };

  // Execution card component with a cuboid effect.
  const ExecutionCard = ({ execution, isHovered }) => {
    const { status, execution_id, workflowTitle, timestamp } = execution;
    console.log("Timestamp:", timestamp)
    const timestampIST = timestamp ? new Date(timestamp + "Z").toLocaleString("en-IN", { 
      hour: 'numeric', 
      minute: 'numeric', 
      hour12: true, 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
  }) : ""
    const lowerStatus = status?.toLowerCase();
    let borderColorClass = "border-zinc-700";
    if (lowerStatus === "running") borderColorClass = "border-yellow-500";
    else if (lowerStatus === "success") borderColorClass = "border-green-500";
    else if (lowerStatus === "failed") borderColorClass = "border-red-500";

    // Status icon based on execution status.
    let statusIcon;
    if (lowerStatus === "running") {
      statusIcon = (
        <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      );
    } else if (lowerStatus === "success") {
      statusIcon = <span className="text-green-500 font-bold">✔</span>;
    } else if (lowerStatus === "failed") {
      statusIcon = <span className="text-red-500 font-bold">✖</span>;
    }

    const depth = 10; // Depth for the 3D effect

    return (
      <div
        className="relative w-full h-full cursor-pointer"
        style={{ transformStyle: "preserve-3d" }}
        onClick={() => handleExecutionClick(execution)}
      >
        {/* Front Face */}
        <div
          className={`absolute inset-0 z-20 bg-gradient-to-br from-zinc-800 to-zinc-700 rounded-2xl border-2 ${borderColorClass} p-4 transition-all duration-200 ease-out
            ${isHovered ? "shadow-[0_12px_20px_rgba(0,0,0,0.8),0_6px_10px_rgba(0,0,0,0.6)]" 
                        : "shadow-[0_4px_6px_rgba(0,0,0,0.3),0_1px_3px_rgba(0,0,0,0.2)]"}`}
          style={{ transform: `translateZ(${depth}px)` }}
        >
          <div className="flex items-center gap-3">
            <div className={`h-3 w-3 rounded-full ${borderColorClass} animate-pulse`} />
            <div>
              <p className="text-sm font-semibold text-zinc-200">
                {workflowTitle || "Workflow"}
              </p>
              <p className="text-xs text-zinc-400">
              {timestampIST}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            {statusIcon}
            <span className="text-xs font-medium text-zinc-400 capitalize">
              {status}
            </span>
          </div>
        </div>
        {/* Top Face */}
        <div
          className="absolute inset-x-0 top-0 z-10 bg-zinc-600 rounded-t-2xl"
          style={{
            height: `${depth}px`,
            transform: `rotateX(90deg) translateZ(${depth / 2}px)`,
            transformOrigin: "top",
          }}
        ></div>
        {/* Right Face */}
        <div
          className="absolute inset-y-0 right-0 z-10 bg-zinc-600 rounded-tr-2xl"
          style={{
            width: `${depth}px`,
            transform: `rotateY(-90deg) translateZ(${depth / 2}px)`,
            transformOrigin: "right",
          }}
        ></div>
      </div>
    );
  };

  // Calculate container height for the 3D stacking effect.
  const containerHeight =
    hoveredIndex === null
      ? mergedExecutions.length * collapsedPeek + fullHeight
      : (() => {
          const lastIndex = mergedExecutions.length - 1;
          return computeTranslateY(lastIndex) + fullHeight;
        })();

  return (
    <>
      {/* Backdrop to disable interaction outside the sidebar */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-40" onClick={onClose}></div>

      <div className="fixed right-0 top-0 h-full w-80 bg-zinc-800 shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="p-4 flex justify-between items-center border-b border-zinc-700">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-semibold text-zinc-100">
              Execution History
            </h2>
            <button
              onClick={fetchExecutionHistory}
              className="text-zinc-300 hover:text-zinc-100"
              title="Refresh History"
            >
              <FiRefreshCw size={20} />
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-300 hover:text-zinc-100"
            title="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex-grow overflow-y-auto">
          {loading ? (
            <div className="text-center text-zinc-400">Loading...</div>
          ) : mergedExecutions.length > 0 ? (
            <div
              className="relative"
              style={{ perspective: "1000px", height: containerHeight }}
            >
              {mergedExecutions.map((exec, index) => (
                <div
                  key={exec.execution_id}
                  style={computeStyle(index)}
                  className="absolute left-0 right-0 transition-all duration-300"
                  onMouseEnter={() => setHoveredIndex(index)}
                >
                  <ExecutionCard execution={exec} isHovered={hoveredIndex === index} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-400">No execution history available.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default ExecutionHistorySidebar;
